import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight, MapPin, AlertTriangle, Truck, Store } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useAuth } from '@/context/AuthContext';
import { formatGs, productImg } from '@/utils/helpers';

export default function CartPage({ onAuthRequired, onSetLocation }) {
  const { items, updateQuantity, removeItem, subtotal, deliveryFee, total, zone, orderType, setOrderType } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const needsZone = orderType === 'delivery' && !zone && !isAuthenticated;
  const canCheckout = orderType === 'pickup' || !!zone || isAuthenticated;

  const handleCheckout = () => {
    if (!isAuthenticated) {
      onAuthRequired();
      return;
    }
    navigate('/checkout');
  };

  if (items.length === 0) {
    return (
      <div data-testid="cart-page" className="pb-nav flex flex-col items-center justify-center min-h-[60vh] px-4 text-center">
        <div className="w-20 h-20 rounded-full bg-zinc-900 flex items-center justify-center mb-4">
          <ShoppingBag size={32} className="text-zinc-600" />
        </div>
        <h2 className="text-xl font-semibold">Tu carrito está vacío</h2>
        <p className="text-zinc-500 text-sm mt-2">Explorá el menú y agregá productos</p>
        <Link to="/menu" className="mt-6 bg-[#E84C1C] text-white font-medium px-8 py-3 rounded-full hover:bg-[#d43f12] transition-all">
          Ver menú
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="cart-page" className="pb-nav">
      <div className="px-4 pt-4 pb-2">
        <h2 className="text-xl font-bold">Tu carrito</h2>
        <p className="text-sm text-zinc-500">{items.length} {items.length === 1 ? 'producto' : 'productos'}</p>
      </div>

      {/* Order type toggle */}
      <div className="px-4 mb-3">
        <div data-testid="cart-order-type-toggle" className="flex gap-2 p-1 bg-zinc-900 rounded-xl border border-zinc-800/50">
          <button
            data-testid="cart-type-delivery"
            onClick={() => setOrderType('delivery')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${orderType === 'delivery' ? 'bg-[#E84C1C] text-white' : 'text-zinc-400 hover:text-zinc-200'}`}
          >
            <Truck size={16} /> Entrega
          </button>
          <button
            data-testid="cart-type-pickup"
            onClick={() => setOrderType('pickup')}
            className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-colors ${orderType === 'pickup' ? 'bg-[#E84C1C] text-white' : 'text-zinc-400 hover:text-zinc-200'}`}
          >
            <Store size={16} /> Retiro en local
          </button>
        </div>
      </div>

      {/* Location warning — delivery only */}
      {needsZone && (
        <div data-testid="cart-location-warning" className="mx-4 mb-3">
          <button
            onClick={onSetLocation}
            className="w-full flex items-center gap-3 p-3.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-left hover:bg-amber-500/15 transition-colors"
          >
            <AlertTriangle size={20} className="text-amber-400 shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-300">Ubicación requerida</p>
              <p className="text-xs text-zinc-400 mt-0.5">Elegí tu ubicación en el mapa para calcular envío y habilitar el pedido</p>
            </div>
            <MapPin size={18} className="text-amber-400 shrink-0" />
          </button>
        </div>
      )}

      <div className="px-4 space-y-3">
        {items.map(item => {
          const addonsCost = item.addons.reduce((s, a) => s + a.extra_cost, 0);
          const lineTotal = (item.unit_price + addonsCost) * item.quantity;
          return (
            <div key={item.key} data-testid={`cart-item-${item.key}`} className="flex gap-3 p-3 bg-zinc-900/50 rounded-xl border border-zinc-800/30">
              {productImg(item.image_url, 'thumb') ? (
                <img src={productImg(item.image_url, 'thumb')} alt={item.name} className="w-16 h-16 rounded-lg object-cover shrink-0 bg-zinc-800" loading="lazy" />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-zinc-800 flex items-center justify-center shrink-0">
                  <span className="text-zinc-600 font-bold text-lg">{item.name[0]}</span>
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{item.name}</p>
                {item.variant_name && (
                  <p className="text-xs text-zinc-400 truncate">{item.variant_name}</p>
                )}
                {item.addons.length > 0 && (
                  <p className="text-xs text-zinc-500 truncate">+ {item.addons.map(a => a.name).join(', ')}</p>
                )}
                {item.promo && (
                  <span className="text-[10px] bg-[#E84C1C]/10 text-[#E84C1C] px-1.5 py-0.5 rounded-full">{item.promo.label}</span>
                )}
                <p className="text-[#E84C1C] text-sm font-bold mt-1">{lineTotal > 0 ? formatGs(lineTotal) : <span className="text-zinc-500 text-xs font-normal">Precio al confirmar zona</span>}</p>
                <div className="flex items-center gap-2 mt-2">
                  <button data-testid={`cart-minus-${item.key}`} onClick={() => updateQuantity(item.key, item.quantity - 1)} className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center hover:bg-zinc-700 transition-colors">
                    <Minus size={14} />
                  </button>
                  <span className="text-sm font-medium w-5 text-center">{item.quantity}</span>
                  <button data-testid={`cart-plus-${item.key}`} onClick={() => updateQuantity(item.key, item.quantity + 1)} className="w-7 h-7 rounded-full bg-zinc-800 flex items-center justify-center hover:bg-zinc-700 transition-colors">
                    <Plus size={14} />
                  </button>
                  <button data-testid={`cart-remove-${item.key}`} onClick={() => removeItem(item.key)} className="ml-auto w-7 h-7 rounded-full bg-red-500/10 flex items-center justify-center hover:bg-red-500/20 transition-colors">
                    <Trash2 size={14} className="text-red-400" />
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Summary */}
      <div className="mt-6 mx-4 p-4 bg-zinc-900 rounded-xl border border-zinc-800/50 space-y-3">
        <div className="flex justify-between text-sm">
          <span className="text-zinc-400">Subtotal</span>
          <span>{formatGs(subtotal)}</span>
        </div>
        <div className="flex justify-between text-sm">
          <span className="text-zinc-400">
            {orderType === 'pickup' ? 'Retiro' : <>Envío {zone && <span className="text-zinc-600">({zone.name})</span>}</>}
          </span>
          <span>
            {orderType === 'pickup'
              ? <span className="text-emerald-400">Gratis</span>
              : zone ? formatGs(deliveryFee) : <span className="text-zinc-600">Pendiente</span>}
          </span>
        </div>
        <div className="border-t border-zinc-800 pt-3 flex justify-between">
          <span className="font-semibold">Total</span>
          <span className="text-[#E84C1C] font-bold text-lg">
            {orderType === 'pickup' ? formatGs(subtotal) : zone ? formatGs(total) : <span className="text-zinc-600">--</span>}
          </span>
        </div>
      </div>

      <div className="px-4 mt-4 space-y-2">
        {!canCheckout ? (
          <button
            data-testid="cart-set-location-btn"
            onClick={onSetLocation}
            className="w-full bg-[#E84C1C] text-white font-medium py-3.5 rounded-full hover:bg-[#d43f12] active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(232,76,28,0.3)]"
          >
            <MapPin size={18} />
            Elegir ubicación para pedir
          </button>
        ) : (
          <button
            data-testid="cart-checkout-btn"
            onClick={handleCheckout}
            className="w-full bg-[#E84C1C] text-white font-medium py-3.5 rounded-full hover:bg-[#d43f12] active:scale-[0.98] transition-all flex items-center justify-center gap-2 shadow-[0_0_20px_rgba(232,76,28,0.3)]"
          >
            {isAuthenticated ? 'Ir al checkout' : 'Ingresar para pedir'}
            <ArrowRight size={18} />
          </button>
        )}
      </div>
    </div>
  );
}
